// ScenarioWizard.jsx
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { authedFetch } from '../../utils/authedFetch';
import {
  Button,
  Typography,
  Modal,
  Box,
  Stack,
  Chip,
  Paper,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import ScenarioDetailsForm from './ScenarioDetailsForm';
import ScenarioEventsForm from './ScenarioEvents';
import ScenarioBloodsForm from './ScenarioBloods';
import ScenarioObs from './ScenarioObs';
import ObservationViewerEditor from './ObsEditor';
import dayjs from 'dayjs';
import { makeId } from '../../utils/generateid';
import FeedbackButton from '../../utils/feedbackButton';

// import EventsForm, SettingsForm if you have them

const SCENARIO_STEPS = ['Scenario Details', 'Bloods', 'Observations'];

export default function ScenarioWizard() {
  const [isLoadingScenarioData, setIsLoadingScenarioData] = useState(false);
  const [savingScenarioData, setSavingScenarioData] = useState(false);
  const { user, session, authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const API_BASE = process.env.REACT_APP_API_URL;
  // Allow either location.state or query string (?patientId=)
  const statePatientId = location.state?.patientId;
  const statePatientName = location.state?.patientName
  //const queryPatientId = new URLSearchParams(location.search).get('patientId');
  const patientId = statePatientId ?? null;

  useEffect(() => {
    if (!patientId) navigate('/app/create/epr', { replace: true });
  }, [patientId, navigate]);

  const [currentStep, setCurrentStep] = useState(0);
  const [saveStatus, setSaveStatus] = useState({});
  const [scenarioData, setScenarioData] = useState({
    scenariodetails: {
        title: '',
        date: dayjs(),
        description: '',
        vignette: '',
        complaint: '',
        length: ''
    },
    events: [],
    scenarioBloods: [],
    scenarioObs: [],
    scenarioImages: [],
    settings: {},
  });

  const currentStepName = SCENARIO_STEPS[currentStep];

  const handleNextPage = () =>
    setCurrentStep(s => Math.min(s + 1, SCENARIO_STEPS.length - 1));

  const handleNextSave = async () => {
    if (currentStepName === 'Scenario Details') {
      const sd = scenarioData.scenariodetails;

      if (!sd.title || !sd.description || !sd.vignette || !sd.complaint) {
        alert('Missing scenario details: title, description, vignette, complaint required.');
        return;
      };

      setSavingScenarioData(true);

      try {
        const userid = 1; 
        const title = scenarioData.scenariodetails.title;
        const query = new URLSearchParams({ title, userid }).toString();
        const res = await authedFetch({
            accessToken: session?.access_token,
            apiBase: API_BASE,
            path: `/api/scenarios?${query}`,
        })

        const existing = await res.json();

        let scenario_id;

        if (existing.exists) {
            alert('Scenario already exists. Please change the scenario title to proceed.');
            setSavingScenarioData(false);
            return;
        } else {
          // Create new scenario
        const saveScenario = await authedFetch({
            accessToken: session?.access_token,
            apiBase: API_BASE,
            path: `/api/scenarios`,
          navigate,
          options: {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
              title: scenarioData.scenariodetails.title,
              description: scenarioData.scenariodetails.description,
              vignette: scenarioData.scenariodetails.vignette,
              complaint: scenarioData.scenariodetails.complaint,
              time: scenarioData.scenariodetails.time,
              user_id: userid,
              patient_id: patientId
          }),
          }});

          const created = await saveScenario.json();
          scenario_id = created.id;
        }

        setScenarioData(prev => ({
          ...prev,
          scenariodetails: { ...prev.scenariodetails, id: scenario_id }
        }));

        setSavingScenarioData(false);
        setSaveStatus(prev => ({ ...prev, [currentStepName]: 'saved' }));
        handleNextPage();
      } catch (e) {
        console.error(e);
        setSaveStatus(prev => ({ ...prev, [currentStepName]: 'unsaved' }));
        alert('Failed to save scenario details.');
      }

    //SAVE BLOOD RESULTS
    } else if (currentStepName === 'Bloods') {
      const { scenarioBloods } = scenarioData;

      const bloodSets = Array.isArray(scenarioBloods)
        ? scenarioBloods
        : [];

      const scenarioid = scenarioData.scenariodetails.id
      setSavingScenarioData(true);
      setSaveStatus(prev => ({ ...prev, [currentStepName]: 'saving' }));

      try {
        // 1. Clear previous bloods
        await fetch(`${API_BASE}/api/scenariobloods/${scenarioid}`, { method: 'DELETE' });

        // 2. Save each blood set
        for (const bloodSet of bloodSets) {
          await fetch(`${API_BASE}/api/save-scenariobloods`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              scenarioid: scenarioid,
              name: bloodSet.name,
              temp: false,
              data: Object.fromEntries(
                bloodSet.groups.map(group => [
                  group.label,
                  group.values.map(test => ({
                    name: test.name,
                    value: test.value,
                    units: test.units,
                    refLow: test.refLow,
                    refHigh: test.refHigh,
                  }))
                ])
              )
            }),
          });
        }

        setSaveStatus(prev => ({ ...prev, [currentStepName]: 'saved' }));
        handleNextPage();
        setSavingScenarioData(false);
      } catch (err) {
        console.error('Error saving bloods:', err);
        alert('Failed to save blood results.');
        setSaveStatus(prev => ({ ...prev, [currentStepName]: 'unsaved' }));
        setSavingScenarioData(false);
      }
    // SAVE OBS
  } else if (currentStepName === 'Observations') {
      const { scenarioObs } = scenarioData

      const scenObs = Array.isArray(scenarioObs)
        ? scenarioObs
        : [];

      const scenarioid = scenarioData.scenariodetails.id
      setSavingScenarioData(true);
      setSaveStatus(prev => ({ ...prev, [currentStepName]: 'saving' }));

      try {
        // 1. Delete previous observations
        await fetch(`${API_BASE}/api/scenarioobs/${scenarioid}`, { method: 'DELETE' });

        // 2. Save each observation
        for (const obs of scenObs) {
          const clean = (value) => value === '' ? null : value;

          await fetch(`${API_BASE}/api/save-scenarioobs`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              scenarioid,
              name: obs.name,
              rr: clean(obs.rr),
              spo2: clean(obs.spo2),
              sbp: clean(obs.sbp),
              pulse: clean(obs.pulse),
              temp: clean(obs.temp),
              avpu: obs.avpu || null,
              air: obs.air || null,
              o2: clean(obs.o2),
              device: obs.device || null
            }),
          });
        }

        setSaveStatus(prev => ({ ...prev, [currentStepName]: 'saved' }));
        setSavingScenarioData(false);
        handleNextPage();
      } catch (err) {
        console.error('Error saving observations:', err);
        alert('Failed to save observations.');
        setSaveStatus(prev => ({ ...prev, [currentStepName]: 'unsaved' }));
        setSavingScenarioData(false);
      }

    } else if (currentStepName === 'Settings') {
      // save settings
      setSaveStatus(prev => ({ ...prev, [currentStepName]: 'saved' }));
    }
  };

  // ---- HYDRATE ON MOUNT / RETURN ----
  useEffect(() => {
    const stateId = location.state?.scenarioId ?? null;
    const scenarioId = stateId ?? null;

    // already hydrated? bail.
    if (!scenarioId || scenarioData.scenariodetails?.id) return;

    (async () => {
      try {
        setIsLoadingScenarioData(true);
        const res = await fetch(`${API_BASE}/api/full-scenario/${scenarioId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const full = await res.json();

        // normalize eprBloods to include a stable client id for UI
        const scenarioBloods = (full.scenarioBloods ?? []).map(set => ({
          ...set,
          id: set.id ?? makeId(),
        }));

        setScenarioData(prev => ({
          ...prev,
          scenariodetails: {
            id: full.scenariodetails.id,
            title: full.scenariodetails.title,
            description: full.scenariodetails.description,
            vignette: full.scenariodetails.vignette,
            complaint: full.scenariodetails.complaint,
            time: full.scenariodetails.time,
            date: full.scenariodetails.date,
          },
          scenarioBloods,
          scenarioObs: full.scenarioObs ?? [],
          events: full.scenarioEvents ?? []
        }));

        setSaveStatus(prev => ({
          ...prev,
          'Scenario Details': 'saved',
          'Bloods': 'saved',
          'Observations': 'saved',
          'Imaging': 'saved',
          'Events': 'saved',
        }));
      } catch (e) {
        setIsLoadingScenarioData(false);
        console.error('Failed to hydrate EPR from patientId:', e);
      }
      setIsLoadingScenarioData(false);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, scenarioData.scenariodetails?.id]);

  const currentStatus = saveStatus[currentStepName];
  const isCurrentStepSaved = currentStatus === 'saved';
  const isCurrentStepSaving = currentStatus === 'saving' || savingScenarioData;
  const isLastStep = currentStep === SCENARIO_STEPS.length - 1;
  const hasScenarioId = !!scenarioData.scenariodetails?.id;

  const primaryButtonLabel = isCurrentStepSaved
    ? isLastStep
      ? 'Finish scenario'
      : 'Next'
    : isLastStep
    ? 'Save'
    : 'Save & next';

  const primaryButtonIcon = isCurrentStepSaved ? (
    <ArrowForwardIcon />
  ) : (
    <SaveOutlinedIcon />
  );

  const handlePrimaryAction = () => {
    if (!isCurrentStepSaved) {
      handleNextSave();
    } else {
      handleNextPage();
    }
  };

  const handleBackToEpr = () => {
    const sid = scenarioData.scenariodetails?.id;

    navigate('/app/create/epr', {
      state: { patientId, scenarioId: sid },
    });
  };

  if (!patientId) return null; // brief guard during redirect

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      {/* Scenario Sidebar */}
      <Box
        sx={{
          width: 250,
          borderRight: '1px solid #E5E7EB',
          p: 2,
          bgcolor: 'white',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 1,
          }}
        >
          <Typography sx={{ fontWeight: 800 }}>
            Scenario
          </Typography>

          <Button
            size="small"
            variant="outlined"
            onClick={handleBackToEpr}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              whiteSpace: 'nowrap',
            }}
          >
            EPR
          </Button>
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 1.5,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          Patient: {statePatientName || '—'}
        </Typography>

        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {SCENARIO_STEPS.map((step, idx) => {
            const status = saveStatus[step];
            const isActive = currentStep === idx;
            const isSaved = status === 'saved';
            const isSaving = status === 'saving';
            const isLocked = !saveStatus['Scenario Details'] && idx !== 0;

            return (
              <li
                key={step}
                onClick={() => {
                  if (isLocked) {
                    alert('You must complete and save scenario information before continuing.');
                    return;
                  }

                  setCurrentStep(idx);
                }}
                style={{
                  listStyle: 'none',
                  marginBottom: 6,
                  opacity: isLocked ? 0.45 : 1,
                  cursor: isLocked ? 'not-allowed' : 'pointer',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    px: 1.25,
                    py: 0.9,
                    borderRadius: 2,
                    bgcolor: isActive ? '#EEF2FF' : 'transparent',
                    border: isActive ? '1px solid #BFDBFE' : '1px solid transparent',
                    transition: '0.15s ease',
                    '&:hover': {
                      bgcolor: isActive ? '#EEF2FF' : '#F8FAFC',
                      borderColor: isActive ? '#BFDBFE' : '#E5E7EB',
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 8,
                      bottom: 8,
                      width: 3,
                      borderRadius: 99,
                      bgcolor: isActive ? '#2563EB' : 'transparent',
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.9rem',
                      fontWeight: isActive ? 800 : 600,
                      color: isLocked ? 'text.disabled' : 'text.primary',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {step}
                  </Typography>

                  <Box
                    sx={{
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      border: isSaved
                        ? '1px solid #16A34A'
                        : isSaving
                        ? '1px solid #60A5FA'
                        : '1px solid #CBD5E1',
                      bgcolor: isSaved
                        ? '#DCFCE7'
                        : isSaving
                        ? '#DBEAFE'
                        : '#F8FAFC',
                    }}
                    title={isSaved ? 'Saved' : isSaving ? 'Saving' : 'Unsaved'}
                  >
                    {isSaving ? (
                      <CircularProgress size={10} />
                    ) : (
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          bgcolor: isSaved ? '#16A34A' : '#CBD5E1',
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </li>
            );
          })}
        </ul>

        <Box sx={{ mt: 2 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={
              isCurrentStepSaving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                primaryButtonIcon
              )
            }
            onClick={handlePrimaryAction}
            disabled={isCurrentStepSaving}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              py: 1,
            }}
          >
            {isCurrentStepSaving ? 'Saving…' : primaryButtonLabel}
          </Button>

          {!isCurrentStepSaved && !isCurrentStepSaving && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.75, textAlign: 'center' }}
            >
              Saves this section before moving on.
            </Typography>
          )}
        </Box>
      </Box>

      <div style={{display:'flex', flexDirection:'column', width:'100%'}}>
        <Paper
          elevation={0}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 20,
            borderBottom: '1px solid #dbeafe',
            bgcolor: 'rgba(255,255,255,0.96)',
            backdropFilter: 'blur(10px)',
            px: 2,
            py: 1.5,
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography sx={{ fontWeight: 800 }}>
                  {currentStepName}
                </Typography>

                {isCurrentStepSaved ? (
                  <Chip
                    size="small"
                    icon={<CheckCircleOutlineIcon />}
                    label="Saved"
                    color="success"
                    variant="outlined"
                  />
                ) : isCurrentStepSaving ? (
                  <Chip
                    size="small"
                    label="Saving…"
                    color="info"
                    variant="outlined"
                  />
                ) : (
                  <Chip
                    size="small"
                    icon={<WarningAmberOutlinedIcon />}
                    label="Unsaved changes"
                    color="warning"
                    variant="outlined"
                  />
                )}

                {hasScenarioId && (
                  <Chip
                    size="small"
                    label={`Scenario #${scenarioData.scenariodetails.id}`}
                    variant="outlined"
                  />
                )}
              </Stack>

              {!isCurrentStepSaved && !isCurrentStepSaving && (
                <Typography variant="body2" color="text.secondary">
                  Save this section before moving on, otherwise your changes may not be stored.
                </Typography>
              )}
            </Stack>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {currentStep > 0 && (
                <Button
                  variant="outlined"
                  onClick={() => setCurrentStep((s) => Math.max(s - 1, 0))}
                  disabled={isCurrentStepSaving}
                  sx={{ textTransform: 'none' }}
                >
                  Back
                </Button>
              )}

              <Button
                variant="contained"
                startIcon={
                  isCurrentStepSaving ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    primaryButtonIcon
                  )
                }
                onClick={handlePrimaryAction}
                disabled={isCurrentStepSaving}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  minWidth: 140,
                  borderRadius: 2,
                }}
              >
                {isCurrentStepSaving ? 'Saving…' : primaryButtonLabel}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <div className='createcontent' style={{ flex: 1, padding: '1rem' }}>
          <h2>{currentStepName}</h2>

          {currentStepName === 'Scenario Details' && !savingScenarioData && (
            <ScenarioDetailsForm
                data={scenarioData.scenariodetails}
                onChange={(patch) => {
                    setScenarioData(p => ({
                    ...p,
                    scenariodetails: { ...p.scenariodetails, ...patch }
                    }));
                    setSaveStatus(ps => ({ ...ps, [currentStepName]: 'unsaved' }));
                }}
                patientId={patientId}
            />
          )}

          {currentStepName === 'Events' && !savingScenarioData && (
            <ScenarioEventsForm
              data={scenarioData.events}
              onChange={(updated) => {
                const prev = JSON.stringify(scenarioData.events);
                const next = JSON.stringify(updated);
                setScenarioData(p => ({ ...p, scenarioData: updated }));
                if (prev !== next) {
                  setSaveStatus(ps => ({ ...ps, [currentStepName]: 'unsaved' }));
                }
              }}
            />
          )}

          {currentStepName === 'Bloods' && !savingScenarioData && (
            <ScenarioBloodsForm
              data={scenarioData.scenarioBloods}
              onChange={(updated) => {
                const prev = JSON.stringify(scenarioData.scenarioBloods);
                const next = JSON.stringify(updated);
                setScenarioData(p => ({ ...p, scenarioBloods: updated }));
                if (prev !== next) {
                  setSaveStatus(ps => ({ ...ps, [currentStepName]: 'unsaved' }));
                }
              }}
            />
          )}

          {currentStepName === 'Observations' && !savingScenarioData && (
            <div style={{display:'flex', width:'100%', justifyContent:'center'}}>
            <div style={{display:'flex', flexDirection:'column', justifyContent:'center', border:'1px solid lightgrey', borderRadius:'6px', width:'90%'}}>
            <ScenarioObs
              data={scenarioData.scenarioObs}
              onChange={(updated) => {
                const prev = JSON.stringify(scenarioData.scenarioObs)
                const next = JSON.stringify(updated)

                if (prev !== next) {
                  setSaveStatus(prevStatus => ({ ...prevStatus, [currentStepName]: 'unsaved' }))
                }
                setScenarioData((prev) => ({ ...prev, scenarioObs: updated }))}
              }
            />
            <div style={{display:'flex', borderBottom:'1px solid lightgrey', marginLeft:'10%', marginRight:'10%', marginTop:'1rem'}}></div>
            <div style={{display:'flex', width:'100%', justifyContent:'center'}}>  
            <ObservationViewerEditor
              data={scenarioData.scenarioObs}
              onChange={(updated) => {
                const prev = JSON.stringify(scenarioData.scenarioObs)
                const next = JSON.stringify(updated)

                if (prev !== next) {
                  setSaveStatus(prevStatus => ({ ...prevStatus, [currentStepName]: 'unsaved' }))
                }
                setScenarioData((prev) => ({ ...prev, scenarioObs: updated }))}
              }
              isScenario={true}
            />
            </div>
            </div>
            </div>
          )}          

          { savingScenarioData && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <CircularProgress size={20} />
              <span>Saving scenario data...</span>
            </div>
          )}

          {/* Add Events and Settings components as needed */}
        </div>
      </div>

      <Box
        sx={{
          position: 'fixed',
          bottom: 20,
          left: 20,
          zIndex: 2000,
        }}
      >
        <FeedbackButton page="Scenario Builder" />
      </Box>

      <Modal open={isLoadingScenarioData}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh'
        }}>
          <CircularProgress />
          <Typography style={{ marginTop: '1rem' }}>Loading patient data...</Typography>
        </div>
      </Modal>
    </div>
  );
}
